import { Box } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import {
  NestedFieldsSelector,
  NestedFieldsField,
} from '../common/fieldsSelectors/NestedFieldsSelector';
import { useState } from 'react';
import { useSweepFields } from '../../sweep-fields/useCachedSweepFields';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import { AssignmentToType } from '../../types/enums/AssignmentToType';
import { ReferenceObjectType } from '../../types/enums/ReferenceObjectType';
import { useSweepFieldsInfo } from '../../sweep-fields/useSweepFieldsInfo';
import { AssignmentsAssignTo } from '../../types/AssignmentsAssignTo';
import { SfdcDisplayType } from '../../types/fields';

interface AssignmentOwnershipSelectorProps {
  crmOrgId: string;
  objectType: string;
  assignTo?: Partial<AssignmentsAssignTo>;
  readonly?: boolean;
  onChange: (assignTo: Partial<AssignmentsAssignTo>) => any;
  excludeFieldIds: string[]; // If provided, the fields with these ids will not be shown in the dropdown
  autoSelectDefault?: boolean;
}

export const AssignmentOwnershipSelector = ({
  crmOrgId,
  objectType,
  assignTo = {},
  readonly,
  onChange,
  excludeFieldIds,
  autoSelectDefault,
}: AssignmentOwnershipSelectorProps) => {
  const { searchSweepFields } = useSweepFields();
  const [fieldMappingObject] = useState(assignTo?.fieldMapping);
  const { getEnrichedNestedPath } = useSweepFieldsInfo();

  const getDefaultOwnerId = async () => {
    const { sweepFields } = await searchSweepFields({
      objectType: [objectType],
      crmOrgId,
    });
    const fields = sweepFields[objectType].fields;
    const filteredFields = fields.filter((el) => !excludeFieldIds?.includes(el.id ?? ''));
    return filteredFields.find((el) => el.sweepFieldName === 'Owner ID');
  };

  useRunOnceWhenTruthy(
    async () => {
      const ownerField = await getDefaultOwnerId();
      ownerField &&
        onChange({
          ...assignTo,
          fieldMapping: {
            ...assignTo.fieldMapping,
            toField: {
              fieldIds: [ownerField.id ?? ''],
            },
          },
        });
    },
    (autoSelectDefault && !fieldMappingObject) || fieldMappingObject?.toField === undefined,
  );

  const isFieldLookupUpdatableAndNotExcluded = (field: SweepField) =>
    !excludeFieldIds?.includes(field.id ?? '') &&
    field.properties?.updateable !== false &&
    field.sfDisplayType !== SfdcDisplayType.ID;

  const getObjectForReference = () => {
    if (assignTo.type === AssignmentToType.Queue) {
      return ReferenceObjectType.Group;
    }
    return ReferenceObjectType.User;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: '14px',
        alignItems: 'center',
      }}
    >
      <Typography variant="body">Choose ownership field:</Typography>
      <Box
        sx={{
          borderRadius: '4px',
          border: `1px solid ${colors.grey[300]}`,
          position: 'relative',
        }}
      >
        <NestedFieldsSelector
          filterBy={isFieldLookupUpdatableAndNotExcluded}
          readonly={readonly}
          deprecated_isReferencedValue={true}
          restrictReferencesToObject={getObjectForReference()}
          disableLookupItemsResolve={true}
          placeholder={'Choose field'}
          crmOrgId={crmOrgId || ''}
          objectType={objectType}
          nestedPath={getEnrichedNestedPath(assignTo?.fieldMapping?.toField?.fieldIds)}
          onChange={(sweepField: NestedFieldsField) => {
            onChange({
              ...assignTo,
              fieldMapping: {
                ...assignTo.fieldMapping,
                toField: {
                  fieldIds: sweepField.fieldIds,
                },
              },
            });
          }}
          forceHideCreateFieldDialog
        />
      </Box>
    </Box>
  );
};
